import type { MinionsPayload, MinionsWeek } from './report';

/*
 * The rates the page shows, derived from the payload's counts. Each is a
 * percentage (0–100) or a ratio, and null when its base is missing or zero —
 * so the page reads "—" rather than a made-up 0.
 */

/** part ÷ whole as a percentage; null without a base. */
export function share(part: number | null | undefined, whole: number | null | undefined): number | null {
  if (part == null || whole == null || whole <= 0) return null;
  return (part / whole) * 100;
}

/** a ÷ b; null without a base. */
export function ratio(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null || b == null || b <= 0) return null;
  return a / b;
}

type T = MinionsPayload['totals'];

/** KPI 2: of the visitors who reached the opt-in screen, the share who accepted and played. */
export const optInCompletion = (t: T) => share(t.uniquePlayers, t.reachedOptIn);
/** KPI 4. */
export const playsPerPlayer = (t: T) => ratio(t.plays, t.uniquePlayers);
/** Average daily players of the last full week ÷ that week's players. */
export const stickiness = (t: T) => share(t.dauLastFullWeek, t.wauLastFullWeek);
export const d1Retention = (t: T) => share(t.d1.returned, t.d1.cohort);
export const d7Retention = (t: T) => share(t.d7.returned, t.d7.cohort);
export const recurringShare = (t: T) => share(t.recurringPlayers, t.uniquePlayers);
/** KPI 10. */
export const loginMissionRate = (t: T) => share(t.loginMission.completed, t.uniquePlayers);
/** KPI 11: players who sent at least one invite. */
export const inviteRate = (t: T) => share(t.invites.inviters, t.uniquePlayers);
/** KPI 12: invited friends who played ÷ invites sent. */
export const inviteConversion = (t: T) => share(t.invites.inviteesPlayed, t.invites.sent);
/** Players whose code reached the invite cap, of all players. */
export const inviteLimitRate = (t: T) => share(t.invites.limitReached, t.uniquePlayers);
export const emailRate = (t: T) => share(t.consents.email, t.optIns);
export const smsRate = (t: T) => share(t.consents.sms, t.optIns);
/** KPI 7, seconds. */
export const avgSessionSec = (t: T) => ratio(t.totalTimeSec, t.sessions);
/** Seconds per player, whole campaign. */
export const timePerPlayerSec = (t: T) => ratio(t.totalTimeSec, t.uniquePlayers);

export const weekPlaysPerPlayer = (w: MinionsWeek) => ratio(w.plays, w.uniquePlayers);
export const weekTimePerPlayerSec = (w: MinionsWeek) => ratio(w.timeSec, w.uniquePlayers);

export type FunnelStep = { key: 'uniqueVisitors' | 'reachedOptIn' | 'optIns' | 'uniquePlayers'; users: number | null };

/** The four funnel steps, first visit to first game. */
export const funnelSteps = (t: T): FunnelStep[] => [
  { key: 'uniqueVisitors', users: t.uniqueVisitors },
  { key: 'reachedOptIn', users: t.reachedOptIn },
  { key: 'optIns', users: t.optIns },
  { key: 'uniquePlayers', users: t.uniquePlayers },
];

/**
 * Each step's loss against the last step before it that is known: null for the
 * first step, for an unknown step, and when the step is not below its
 * predecessor. `worst` is the step with the biggest loss (−1 when none).
 */
export function funnelLosses(steps: FunnelStep[]): { losses: Array<number | null>; worst: number } {
  let prev: number | null = null;
  const losses = steps.map((s) => {
    if (s.users == null) return null;
    const loss = prev != null && prev > s.users ? prev - s.users : null;
    prev = s.users;
    return loss;
  });
  let worst = -1;
  losses.forEach((l, i) => {
    if (l != null && (worst < 0 || l > (losses[worst] ?? 0))) worst = i;
  });
  return { losses, worst };
}

/** The week running now, else the last one that has figures. */
export const currentWeek = (weeks: MinionsWeek[]) => weeks.find((w) => w.status === 'live') ?? [...weeks].reverse().find((w) => w.status === 'done') ?? null;
